import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ExternalLink, MapPin, Building2 } from "lucide-react";
import {
  getEditorBySlug,
  allEditors,
} from "@/data/editorialBoards";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/SEO";

function initials(name) {
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("");
}

export default function EditorProfile() {
  const { slug } = useParams();
  const editor = getEditorBySlug(slug);

  if (!editor) {
    return <Navigate to="/" replace />;
  }

  // Six other editors from the same journal for the side panel
  const peers = allEditors()
    .filter((e) => e.journalId === editor.journalId && e.slug !== editor.slug)
    .slice(0, 6);

  // Person JSON-LD with ORCID sameAs and affiliated organization
  const personLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: editor.name,
    jobTitle: editor.role,
    affiliation: {
      "@type": "Organization",
      name: editor.affiliation,
    },
    nationality: editor.country,
    ...(editor.orcid && {
      identifier: {
        "@type": "PropertyValue",
        propertyID: "ORCID",
        value: editor.orcid,
      },
      sameAs: [`https://orcid.org/${editor.orcid}`],
    }),
    worksFor: {
      "@type": "Organization",
      name: "International Sci Ink Press Ltd",
      url: "https://journalmedica.com",
    },
  };

  const description = `${editor.name} — ${editor.role} at ${editor.journalShort} (International Sci Ink Press). ${editor.affiliation}. Based in ${editor.country}.`;

  return (
    <div className="bg-[var(--sci-bg)]">
      <SEO
        title={`${editor.name} — ${editor.journalShort} Editorial Board`}
        description={description}
        path={`/editor/${editor.slug}`}
        type="profile"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Journals", url: "/#journals" },
          {
            name: `${editor.journalShort} Editorial Board`,
            url: `/editorial-board/${editor.journalId}`,
          },
          { name: editor.name, url: `/editor/${editor.slug}` },
        ]}
        jsonLd={[personLd]}
      />
      <Navbar />

      <section className="relative pt-32 md:pt-40 pb-12 md:pb-16 bg-[var(--sci-bg)] bg-grid-faint border-b border-[var(--sci-border)] overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to={`/editorial-board/${editor.journalId}`}
            data-testid="editor-back-board"
            className="inline-flex items-center gap-2 text-xs font-mono-sci tracking-wider text-[var(--sci-text-muted)] hover:text-[var(--sci-teal)] transition-colors mb-8"
          >
            <ArrowLeft size={12} />
            {editor.journalShort} EDITORIAL BOARD
          </Link>

          <div className="grid md:grid-cols-12 gap-10 items-start">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="md:col-span-4 relative"
            >
              <div className="aspect-square bg-[var(--sci-navy)] text-white flex items-center justify-center overflow-hidden relative">
                <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" />
                <div
                  className="absolute -right-20 -top-20 w-72 h-72 rounded-full pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(closest-side, rgba(0,122,138,0.4), transparent)",
                  }}
                />
                <div
                  className="relative font-serif-display tracking-tight text-center"
                  style={{ fontSize: "9rem", lineHeight: "1" }}
                >
                  {initials(editor.name)}
                </div>
                <div className="absolute bottom-4 right-4 overline text-white/70 text-[10px]">
                  {editor.country}
                </div>
              </div>
            </motion.div>

            <div className="md:col-span-8">
              <div className="overline text-[var(--sci-teal)] mb-3">
                {editor.role} · {editor.journalShort}
              </div>
              <h1
                className="font-serif-display text-4xl md:text-6xl text-[var(--sci-navy)] tracking-tight leading-[1.02]"
                data-testid="editor-name"
              >
                {editor.name}
              </h1>
              <p
                className="mt-6 text-[var(--sci-text-muted)] leading-relaxed text-base md:text-lg max-w-2xl"
                data-testid="editor-affiliation"
              >
                {editor.affiliation}
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3 text-xs">
                <div className="inline-flex items-center gap-2 border border-[var(--sci-border)] px-3 py-1.5 font-mono-sci tracking-wider">
                  <MapPin size={11} className="text-[var(--sci-teal)]" />
                  {editor.country}
                </div>
                <div className="inline-flex items-center gap-2 border border-[var(--sci-border)] px-3 py-1.5 font-mono-sci tracking-wider">
                  <Building2 size={11} className="text-[var(--sci-teal)]" />
                  {editor.journalShort}
                </div>
                {editor.orcid && (
                  <a
                    href={`https://orcid.org/${editor.orcid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="editor-orcid"
                    className="inline-flex items-center gap-2 border border-[var(--sci-border)] px-3 py-1.5 font-mono-sci tracking-wider text-[var(--sci-navy)] hover:text-[var(--sci-teal)] hover:border-[var(--sci-teal)] transition-colors"
                  >
                    ORCID · {editor.orcid}
                    <ExternalLink size={10} />
                  </a>
                )}
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to={`/editorial-board/${editor.journalId}`}
                  data-testid="editor-cta-board"
                  className="btn-primary inline-flex items-center gap-2 px-5 py-3 text-sm tracking-wide"
                >
                  Full {editor.journalShort} board
                  <ArrowUpRight size={14} />
                </Link>
                <Link
                  to="/#submit"
                  data-testid="editor-cta-submit"
                  className="btn-secondary inline-flex items-center gap-2 px-5 py-3 text-sm tracking-wide"
                >
                  Submit to {editor.journalShort}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Peer editors */}
      {peers.length > 0 && (
        <section className="py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="overline text-[var(--sci-teal)] mb-5">
              Also on the {editor.journalShort} editorial board
            </div>
            <h2 className="font-serif-display text-2xl md:text-3xl text-[var(--sci-navy)] tracking-tight leading-tight mb-10">
              Fellow editors at {editor.journalShort}.
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {peers.map((p) => (
                <Link
                  key={p.slug}
                  to={`/editor/${p.slug}`}
                  data-testid={`peer-editor-${p.slug}`}
                  className="group flex items-start gap-4 bg-white border border-[var(--sci-border)] p-5 hover:border-[var(--sci-teal)] transition-colors"
                >
                  <div className="w-12 h-12 shrink-0 bg-[var(--sci-bg)] border border-[var(--sci-border)] flex items-center justify-center font-serif-display text-lg text-[var(--sci-navy)] group-hover:bg-[var(--sci-navy)] group-hover:text-white transition-colors">
                    {initials(p.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-serif-display text-base text-[var(--sci-navy)] leading-tight group-hover:text-[var(--sci-teal)] transition-colors">
                      {p.name}
                    </div>
                    <div className="text-xs text-[var(--sci-text-muted)] mt-1.5 leading-snug line-clamp-2">
                      {p.role}
                    </div>
                    <div className="mt-2 font-mono-sci text-[10px] tracking-wider text-[var(--sci-teal)]">
                      {p.country}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
